import React, { useState } from 'react';
import { HideEye } from 'components/AccountLoginRegistration/Icons/HideEye';
import { ShowEye } from 'components/AccountLoginRegistration/Icons/ShowEye';
import './styles.themed.scss';

/**
 * A password field used for Formik <Field/>, allowing users to toggle visibility of the password.
 * @param {object} props
 * @param {string} props.dataTestId Use to query element for testing purpose
 * @param {{
 *  onBlur: () => void,
 *  onChange: () => void,
 *  value: string,
 *  name: string,
 * }} props.field https://formik.org/docs/api/useField#fieldinputpropsvalue
 * @param {string} props.placeholder Specifies a short hint that describes the expected value of an <input> element
 * @param {Function} [props.onKeyDown] Optional keydown handler (e.g. for Enter key)
 * @param {string} [props.value] Optional value to control the input
 * @param {Function} [props.onChange] Optional change handler for the input
 * @param {Function} [props.onBlur] Optional blur handler for the input
 * @param {boolean} props.hasError Flag to indicate if an error occurred from user input
 * @param {string} [props.name] Optional name for the input field, defaults to 'password'
 * @returns {React.ReactElement}
 */
const PasswordField = (props) => {
  const {
    dataTestId = 'password-field',
    field = {},
    hasError = false,
    placeholder = '',
    onKeyDown = () => {},
    value,
    onChange,
    onBlur,
    name,
  } = props;

  const inputValue = value !== undefined ? value : field.value;
  const inputOnChange = onChange || field.onChange || (() => {});
  const inputOnBlur = onBlur || field.onBlur || (() => {});
  const inputName = name || field.name || 'password';

  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="passwordField">
      <input
        id={inputName}
        data-testid={dataTestId}
        name={inputName}
        placeholder={placeholder}
        type={showPassword ? 'text' : 'password'}
        aria-label="password"
        value={inputValue}
        onBlur={inputOnBlur}
        onChange={inputOnChange}
        onKeyDown={onKeyDown}
        className={hasError ? 'errorInput' : ''}
      />
      <button
        type="button"
        data-testid="toggle-password-visibility"
        aria-label="toggle password visibility"
        onClick={() => setShowPassword(!showPassword)}
        className="eye"
      >
        {showPassword ? <HideEye /> : <ShowEye />}
      </button>
    </div>
  );
};

export { PasswordField };
